// src/components/pages/blotter/converts/CreateBlotterEntry.js
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { createBlotterEntry } from "../../../../services/Axios";

const ACTION_OPTIONS = ["Buy", "Sell", "Short", "Cover"];

export default function CreateBlotterEntry() {
  const theme = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    trade_date: new Date().toISOString().split("T")[0],
    strategy_name: "",
    security_symbol: "",
    hedge_symbol: "",
    action: "",
    hedge_quantity: "",
    hedge_price: "",
    notes: {},
    enter_hedge: false,
    hedge_entered: false,
    not_convert: false,
  });

  const [notes, setNotes] = useState([{ key: "", value: "" }]);
  const [hedgeAction, setHedgeAction] = useState("");
  const [hedgeEntryText, setHedgeEntryText] = useState("");
  const [hedgeReviewed, setHedgeReviewed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Calculate hedge action based on security action
  useEffect(() => {
    const getHedgeAction = (action) => {
      switch (action) {
        case "Buy":
          return "Short";
        case "Sell":
          return "Cover";
        case "Short":
          return "Buy";
        case "Cover":
          return "Sell";
        default:
          return "";
      }
    };

    const action = getHedgeAction(formData.action);
    setHedgeAction(action);

    if (
      action &&
      formData.hedge_quantity &&
      formData.hedge_symbol &&
      formData.hedge_price
    ) {
      setHedgeEntryText(
        `${action} ${formData.hedge_quantity} of ${formData.hedge_symbol} at ${formData.hedge_price}`,
      );
    } else {
      setHedgeEntryText("");
    }
  }, [
    formData.action,
    formData.hedge_quantity,
    formData.hedge_symbol,
    formData.hedge_price,
  ]);

  const handleInputChange = (field, value) => {
    // Normalize text by uppercasing string fields
    const normalizedValue =
      typeof value === "string" && field !== "trade_date"
        ? value.toUpperCase()
        : value;
    setFormData((prev) => ({
      ...prev,
      [field]: normalizedValue,
    }));
  };

  const handleNotesChange = (index, field, value) => {
    const newNotes = [...notes];
    newNotes[index][field] = value.toUpperCase();
    setNotes(newNotes);
  };

  const addNoteField = () => {
    setNotes([...notes, { key: "", value: "" }]);
  };

  const removeNoteField = (index) => {
    if (notes.length > 1) {
      const newNotes = notes.filter((_, i) => i !== index);
      setNotes(newNotes);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.enter_hedge && !hedgeReviewed) {
      setError("Please review the hedge entry before saving");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Convert notes array to object
      const notesObject = {};
      notes.forEach((note) => {
        if (note.key && note.value) {
          notesObject[note.key] = note.value;
        }
      });

      const submitData = {
        ...formData,
        notes: notesObject,
      };

      await createBlotterEntry(submitData);
      navigate("/Converts_Blotter");
    } catch (err) {
      setError("Failed to create blotter entry: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/Converts_Blotter");
  };

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 }, maxWidth: 1400, mx: "auto", mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
        Create Blotter Entry
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          {/* Left Column - Main Form Fields */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={1}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Basic Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Trade Date"
                          type="date"
                          value={formData.trade_date}
                          onChange={(e) =>
                            handleInputChange("trade_date", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Strategy Name"
                          value={formData.strategy_name}
                          onChange={(e) =>
                            handleInputChange("strategy_name", e.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" required>
                          <InputLabel>Action</InputLabel>
                          <Select
                            value={formData.action}
                            label="Action"
                            onChange={(e) =>
                              handleInputChange("action", e.target.value)
                            }
                          >
                            {ACTION_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Broker"
                          value={formData.broker}
                          onChange={(e) =>
                            handleInputChange("broker", e.target.value)
                          }
                          required
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Security Information */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Security Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Security Symbol"
                          value={formData.security_symbol}
                          onChange={(e) =>
                            handleInputChange("security_symbol", e.target.value)
                          }
                          required
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Hedge Information */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Hedge Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Symbol"
                          value={formData.hedge_symbol}
                          onChange={(e) =>
                            handleInputChange("hedge_symbol", e.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Quantity"
                          type="number"
                          value={formData.hedge_quantity}
                          onChange={(e) =>
                            handleInputChange("hedge_quantity", e.target.value)
                          }
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Hedge Price"
                          type="number"
                          step="0.01"
                          value={formData.hedge_price}
                          onChange={(e) =>
                            handleInputChange("hedge_price", e.target.value)
                          }
                          required
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Notes (Key-Value Pairs)
                    </Typography>
                    {notes.map((note, index) => (
                      <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <TextField
                          size="small"
                          label="Key"
                          value={note.key}
                          onChange={(e) =>
                            handleNotesChange(index, "key", e.target.value)
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          value={note.value}
                          onChange={(e) =>
                            handleNotesChange(index, "value", e.target.value)
                          }
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          onClick={() => removeNoteField(index)}
                          disabled={notes.length === 1}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addNoteField}
                      variant="outlined"
                      size="small"
                    >
                      Add Note Field
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - Calculated Fields and Hedge Entry */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              {/* Calculated Fields */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Calculated Fields
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Delta"
                          value={formData.delta}
                          InputProps={{ readOnly: true }}
                          helperText="(hedge qty) / (security qty * cr)"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="LMV"
                          value={formData.lmv}
                          InputProps={{ readOnly: true }}
                          helperText="security qty * security price * 10"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="SMV"
                          value={formData.smv}
                          InputProps={{ readOnly: true }}
                          helperText="hedge qty * hedge price"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Delta Adjustment"
                          value={formData.delta_adjustment}
                          InputProps={{ readOnly: true }}
                          helperText="((close-hedge price)*cr)*delta/10"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Nuke"
                          value={formData.nuke}
                          InputProps={{ readOnly: true }}
                          helperText="LMV - SMV"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Hedge Entry */}
              <Grid item xs={12}>
                <Card sx={{ mb: 1 }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ mb: 1, fontSize: "1rem" }}
                    >
                      Hedge Entry
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.enter_hedge}
                          onChange={(e) =>
                            handleInputChange("enter_hedge", e.target.checked)
                          }
                        />
                      }
                      label="Enter Hedge"
                    />
                    {formData.enter_hedge && hedgeEntryText && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Hedge Entry to Review:
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {hedgeEntryText}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={hedgeReviewed}
                              onChange={(e) =>
                                setHedgeReviewed(e.target.checked)
                              }
                            />
                          }
                          label="I have reviewed this hedge entry"
                          sx={{ mt: 1 }}
                        />
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                size="small"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading || (formData.enter_hedge && !hedgeReviewed)}
                size="small"
              >
                {loading ? "Creating..." : "Create Entry"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
